import React, { useEffect, useState } from "react";
import { ArrowLeft, BarChart3 } from "lucide-react";
import { moduleApi } from "../../lib/api";
import { formatEuro, mapReservationFromApi, type Board, type Reservation } from "./types";

const parseDate = (d: string) => {
  if (!d) return NaN;
  if (d.includes("/")) {
    const parts = d.split("/");
    if (parts.length !== 3) return NaN;
    const [dd, mm, yyyy] = parts.map(Number);
    if (!dd || !mm || !yyyy) return NaN;
    return new Date(yyyy, mm - 1, dd).getTime();
  }
  if (d.includes("-")) {
    const parts = d.split("-");
    if (parts.length !== 3) return NaN;
    const [yyyy, mm, dd] = parts.map(Number);
    if (!dd || !mm || !yyyy) return NaN;
    return new Date(yyyy, mm - 1, dd).getTime();
  }
  const ts = new Date(d).getTime();
  return Number.isNaN(ts) ? NaN : ts;
};

const inputCls = "w-full bg-surface border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors";
const labelCls = "text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block";

export function GlobalSummary({ boards, onBack }: { boards: Board[]; onBack: () => void }) {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [serverFilter, setServerFilter] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const data = await moduleApi.getExceptionServiceReservations();
        if (Array.isArray(data)) {
          setReservations(data.map((r: any) => mapReservationFromApi(r, boards.find((b) => b.id === r.board_id)?.title)));
        }
      } catch (e) {
        console.error("Failed to load global reservations", e);
      }
    })();
  }, [boards]);

  const filtered = reservations.filter((r) => {
    if (serverFilter) {
      const s = serverFilter.toLowerCase();
      if (!r.takenBy.toLowerCase().includes(s) && !r.cashedBy.toLowerCase().includes(s)) return false;
    }
    const createdTs = parseDate(r.createdDate);
    if (startDate) {
      const minTs = parseDate(startDate);
      if (!isNaN(minTs) && createdTs < minTs) return false;
    }
    if (endDate) {
      const maxTs = parseDate(endDate);
      if (!isNaN(maxTs) && createdTs > maxTs) return false;
    }
    if (paymentFilter) {
      const map: Record<string, number> = { cb: r.cb, amex: r.amex, cash: r.cash, cheque: r.cheque, zen: r.zen, virm: r.virm };
      const value = map[paymentFilter.toLowerCase()];
      if (!value || value <= 0) return false;
    }
    return true;
  });

  const totalTheoretical = filtered.reduce((sum, r) => sum + r.adults * r.tarifAdult + r.children * r.tarifChild, 0);
  const totalCb = filtered.reduce((sum, r) => sum + r.cb, 0);
  const totalAmex = filtered.reduce((sum, r) => sum + r.amex, 0);
  const totalCash = filtered.reduce((sum, r) => sum + r.cash, 0);
  const totalCheque = filtered.reduce((sum, r) => sum + r.cheque, 0);
  const totalZen = filtered.reduce((sum, r) => sum + r.zen, 0);
  const totalVirm = filtered.reduce((sum, r) => sum + r.virm, 0);
  const totalEncaisse = totalCb + totalAmex + totalCash + totalCheque + totalZen + totalVirm;
  const totalReste = totalTheoretical - totalEncaisse;
  const totalCovers = filtered.reduce((sum, r) => sum + r.adults + r.children, 0);

  const stats = [
    { label: "CA théorique total", value: formatEuro(totalTheoretical), color: "text-content" },
    { label: "Total encaissé", value: formatEuro(totalEncaisse), color: "text-emerald-500" },
    { label: "Reste à encaisser", value: formatEuro(totalReste), color: "text-warning-500" },
    { label: "Nombre total de couverts", value: String(totalCovers), color: "text-content" },
  ];

  return (
    <div className="space-y-8">
      <header className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-brand-500/15 flex items-center justify-center text-brand-500">
          <BarChart3 size={22} />
        </div>
        <div>
          <h1 className="text-2xl font-black text-content">Vue globale</h1>
          <p className="text-sm text-content-muted mt-1">CA et couverts tous tableaux confondus, filtrable par date, serveur et moyen de paiement.</p>
        </div>
      </header>

      <button onClick={onBack} className="flex items-center gap-2 text-sm font-bold text-content-muted hover:text-content transition-colors">
        <ArrowLeft size={16} /> Retour aux tableaux
      </button>

      <div className="bg-surface border border-border rounded-2xl p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className={labelCls}>Date mini (création)</label>
          <input type="text" placeholder="JJ/MM/AAAA" className={inputCls} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Date maxi (création)</label>
          <input type="text" placeholder="JJ/MM/AAAA" className={inputCls} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Encaisser/Prise par</label>
          <input type="text" placeholder="Nom du serveur" className={inputCls} value={serverFilter} onChange={(e) => setServerFilter(e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Moyen de paiement</label>
          <select className={inputCls} value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}>
            <option value="">Tous</option>
            <option value="cb">CB</option>
            <option value="amex">AMEX</option>
            <option value="cash">ESPECE</option>
            <option value="cheque">CHEQUE</option>
            <option value="zen">ZEN</option>
            <option value="virm">VIRM</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        {stats.map((s, idx) => (
          <div key={idx} className="bg-surface border border-border rounded-2xl p-5">
            <div className="text-sm text-content-muted mb-2">{s.label}</div>
            <div className={`text-2xl font-black ${s.color}`}>{s.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
